import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../../services/api';
import { UserPlus, CalendarPlus, Trash2, ArrowLeft } from 'lucide-react';
import AddMemberModal from './components/AddMemberModal';
import AddEventTypeModal from './components/AddEventTypeModal';
import ConfirmationModal from '../components/ConfirmationModal';

// --- API Functions ---
const fetchTeam = async (id) => (await api.get(`/agenda/equipes/${id}`)).data.data;
const fetchMembers = async (id) => (await api.get(`/agenda/equipes/${id}/membros`)).data.data;
const fetchEventTypes = async (id) => (await api.get(`/agenda/equipes/${id}/tipos-de-evento`)).data.data;

const addMember = async ({ teamId, userId }) => (await api.post(`/agenda/equipes/${teamId}/membros`, { user_id: userId })).data.data;
const removeMember = async ({ teamId, userId }) => (await api.delete(`/agenda/equipes/${teamId}/membros/${userId}`)).data.data;

const addEventType = async ({ teamId, typeId }) => (await api.post(`/agenda/equipes/${teamId}/tipos-de-evento`, { tipo_de_evento_id: typeId })).data.data;
const removeEventType = async ({ teamId, typeId }) => (await api.delete(`/agenda/equipes/${teamId}/tipos-de-evento/${typeId}`)).data.data;


export default function TeamDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [isAddTypeOpen, setIsAddTypeOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmData, setConfirmOpenData] = useState({ id: null, type: '' });

  // --- Queries ---
  const { data: team, isLoading: isLoadingTeam } = useQuery({ queryKey: ['team', id], queryFn: () => fetchTeam(id) });
  const { data: members, isLoading: isLoadingMembers } = useQuery({ queryKey: ['teamMembers', id], queryFn: () => fetchMembers(id) });
  const { data: teamTypes, isLoading: isLoadingTypes } = useQuery({ queryKey: ['teamTypes', id], queryFn: () => fetchEventTypes(id) });

  // --- Mutations ---
  const addMemberMutation = useMutation({
    mutationFn: addMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers', id] });
      setIsAddMemberOpen(false);
    }
  });

  const removeMemberMutation = useMutation({
    mutationFn: removeMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers', id] });
      setIsConfirmOpen(false);
    }
  });

  const addTypeMutation = useMutation({
    mutationFn: addEventType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamTypes', id] });
      setIsAddTypeOpen(false);
    }
  });

  const removeTypeMutation = useMutation({
    mutationFn: removeEventType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamTypes', id] });
      setIsConfirmOpen(false);
    }
  });

  // --- Handlers ---
  const handleRemoveClick = (itemId, type) => {
    setConfirmOpenData({ id: itemId, type });
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (confirmData.type === 'member') {
      removeMemberMutation.mutate({ teamId: id, userId: confirmData.id });
    } else {
      removeTypeMutation.mutate({ teamId: id, typeId: confirmData.id });
    }
  };

  if (isLoadingTeam) return <div className="p-8 text-center text-gray-500">Carregando detalhes...</div>;
  if (!team) return <div className="p-8 text-center text-red-500">Equipe não encontrada.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/agenda')} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Equipe: {team.nome}</h1>
            <p className="text-sm text-gray-500">Gerencie quem atende e quais serviços esta equipe presta.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Members Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">Membros da Equipe</h2>
            <button onClick={() => setIsAddMemberOpen(true)} className="flex items-center gap-2 text-primary hover:text-secondary text-sm font-medium transition-colors">
              <UserPlus size={18} />
              Adicionar
            </button>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[500px]">
            {isLoadingMembers ? <p className="p-6 text-gray-400">Carregando membros...</p> : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Nome</th>
                    <th className="text-right py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {members?.map(m => (
                    <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6">
                        <div className="text-sm font-medium text-gray-900">{m.nome}</div>
                        <div className="text-xs text-gray-500">{m.email}</div>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <button onClick={() => handleRemoveClick(m.id, 'member')} className="text-red-500 hover:text-red-700 transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {members?.length === 0 && (
                    <tr><td colSpan="2" className="p-6 text-center text-gray-400 text-sm">Nenhum membro adicionado.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Event Types Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">Tipos de Evento</h2>
            <button onClick={() => setIsAddTypeOpen(true)} className="flex items-center gap-2 text-primary hover:text-secondary text-sm font-medium transition-colors">
              <CalendarPlus size={18} />
              Associar
            </button>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[500px]">
            {isLoadingTypes ? <p className="p-6 text-gray-400">Carregando tipos...</p> : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Serviço</th>
                    <th className="text-right py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {teamTypes?.map(t => (
                    <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6">
                        <div className="text-sm font-medium text-gray-900">{t.nome}</div>
                        <div className="text-xs text-gray-500">{t.duracao} minutos</div>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <button onClick={() => handleRemoveClick(t.id, 'type')} className="text-red-500 hover:text-red-700 transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {teamTypes?.length === 0 && (
                    <tr><td colSpan="2" className="p-6 text-center text-gray-400 text-sm">Nenhum tipo de evento associado.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <AddMemberModal isOpen={isAddMemberOpen} onClose={() => setIsAddMemberOpen(false)} onAdd={(uid) => addMemberMutation.mutate({ teamId: id, userId: uid })} />
      <AddEventTypeModal isOpen={isAddTypeOpen} onClose={() => setIsAddTypeOpen(false)} onAdd={(tid) => addTypeMutation.mutate({ teamId: id, typeId: tid })} />
      
      <ConfirmationModal 
        isOpen={isConfirmOpen} 
        onClose={() => setIsConfirmOpen(false)} 
        onConfirm={handleConfirmDelete}
        title="Confirmar Remoção"
        message={`Tem certeza que deseja remover este ${confirmData.type === 'member' ? 'membro' : 'tipo de evento'} da equipe?`}
      />
    </div>
  );
}
